import { Box, Button, Container, Group, Loader, Stack, Text, Title, Alert } from "@mantine/core";
import { Link, useParams } from "react-router-dom";
import { useFranchiseMatchesQuery, MatchType } from "generated/graphql";
import UserMatchFeed, { MatchItem } from "components/profile/UserMatchFeed";

/**
 * FranchiseMatches
 *
 * Franchise-scoped matches page. Route: /franchises/:franchiseId/matches
 * - Shows a back button that returns to the franchise profile
 * - Uses the FranchiseMatches GraphQL query to fetch all matches for teams under the franchise
 */
export default function FranchiseMatches() {
  const { franchiseId } = useParams<{ franchiseId: string }>();

  const { data, loading, error } = useFranchiseMatchesQuery({
    variables: {
      franchiseId: franchiseId ?? "",
      limit: 20,
      offset: 0,
    },
    skip: !franchiseId,
  });

  const backHref = `/franchises/${franchiseId ?? ""}`;

  // Transform API data to MatchItem format
  const items: MatchItem[] = (data?.franchiseMatches ?? []).map((match) => {
    const isCompleted = match.status === "Completed";
    const homeTeam = match.gameSeries?.homeTeam?.name ?? "Team A";
    const awayTeam = match.gameSeries?.awayTeam?.name ?? "Team B";

    // For franchise matches, show both teams as "Home vs Away"
    const opponent = `${homeTeam} vs ${awayTeam}`;

    // Determine result based on the franchise's team
    let result: "W" | "L" | "D" | undefined = undefined;
    if (isCompleted && match.winningTeam) {
      // Note: For franchise view, we show the general result
      // The actual W/L determination would need to check which team belongs to this franchise
      result = match.homeTeamScore === match.awayTeamScore ? "D" : (match.winningTeam === "TEAM_A" ? "W" : "L");
    }

    return {
      id: match.id,
      opponent,
      scheduledAt: match.scheduledTime ?? match.startTime ?? new Date().toISOString(),
      result: isCompleted ? result : undefined,
      score: match.homeTeamScore !== null && match.awayTeamScore !== null
        ? `${match.homeTeamScore}-${match.awayTeamScore}`
        : undefined,
      tournament: match.tier?.tierName ?? match.matchType ?? "Match",
      type: isCompleted ? "past" : "upcoming",
    };
  });

  return (
    <Container size="lg" p="md">
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Title order={3}>Franchise Matches</Title>
          <Button component={Link} to={backHref} variant="light">
            Back to Franchise
          </Button>
        </Group>

        {loading && (
          <Group justify="center" p="xl">
            <Loader size="lg" />
            <Text>Loading matches...</Text>
          </Group>
        )}

        {error && (
          <Alert color="red" title="Error loading matches">
            {error.message}
          </Alert>
        )}

        {!loading && !error && (
          <Box>
            <UserMatchFeed
              title="Franchise Match Feed"
              items={items}
              emptyMessage="No matches found for this franchise."
            />
          </Box>
        )}
      </Stack>
    </Container>
  );
}
